import React from 'react'

import TableFilterPill from './TableFilterPill/TableFilterPill'
import { Button, Icon, Tooltip } from '../../../module'
import styles from './_table-filter.module.scss'
import { c } from '../../../translations/LibraryTranslationService'

export type ActiveTableFilterObj = {
  /** The value, or list of values, of an active filter. */
  value: number | number[] | string[]
  /** The comparison value of an active filter. */
  comparison_value: string
  /** The label of the active filter. */
  label: string
}

export type ActiveTableFilterProps = {
  [key: string]: ActiveTableFilterObj
}

export type TableFilterProps = {
  activeFilters: ActiveTableFilterProps
  remove: (filter?: string) => void
  /** Optional prop to add a test id to the TableFilter for QA testing */
  qaTestId?: string
}

/**
 * TableFilter component displays active filters and allows users to remove them.
 * It also provides a button to clear all filters.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.activeFilters - An object containing the active filters.
 * @param {Function} props.remove - A function to remove a specific filter or all filters.
 *
 * @returns {React.JSX.Element} The rendered TableFilter component.
 */
const TableFilter = ({
  activeFilters,
  remove,
  qaTestId = 'table-filter',
}: TableFilterProps): React.JSX.Element => {
  return (
    <div className={styles.container} qa-test-id={qaTestId}>
      <Icon icon='filter' color='purple' />
      <div className={styles.pillContainer}>
        {Object.keys(activeFilters).length > 0 ? (
          Object.keys(activeFilters).map((af) => (
            <span key={af}>
              {Array.isArray(activeFilters[af].value) ? (
                <Tooltip
                  position='top'
                  noPadding
                  tooltipContent={
                    <div className={`${styles.pillTooltipContainer}`}>
                      <ul className={`${styles.pillTooltip} `}>
                        {(activeFilters[af].value as (number | string)[])?.map(
                          (item, index) => (
                            <li key={index}>{item}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  }
                  maxWidth='250px'
                >
                  <div>
                    <TableFilterPill
                      // TODO: Check with UX to see if we should add a dynamic count for the label - ex: Label (3)
                      text={activeFilters[af].label}
                      remove={() => remove(af)}
                    />
                  </div>
                </Tooltip>
              ) : (
                <TableFilterPill
                  text={activeFilters[af].label}
                  remove={() => remove(af)}
                />
              )}
            </span>
          ))
        ) : (
          <span className={styles.noFilter}>{c('noFiltersApplied')}</span>
        )}
      </div>
      {Object.keys(activeFilters).length > 0 && (
        <Button styleType='text-blue' onClick={() => remove()}>
          {c('clear')}
        </Button>
      )}
    </div>
  )
}

export default TableFilter
